import dynamic from "next/dynamic"
import { loadCustomComponent } from "lib/componentLoader"
import { BlockStateProvider } from "providers/blockState"

// Static imports: SSR-rendered content components (dynamic() hydration is broken in Next.js 15 for SSR chunks)
import Body from "components/matrix/body"
import Ad from "components/matrix/ad"
import Accordion from "components/matrix/accordion"
import Tabs from "components/matrix/tabs"
import Hero from "components/matrix/hero"
import Row from "components/matrix/row"
import TwoColumn from "components/matrix/twoColumn"
import WrapperBlock from "components/matrix/wrapper"
import SinglesH1Heading from "components/header/singlesH1Heading"

// Dynamic with ssr:false: components that fetch their own data and render a loader on first paint anyway
const AgendaSession = dynamic(() => import("components/matrix/agendaSession"), { ssr: false })
const AdvancedAgenda = dynamic(() => import("components/matrix/advancedAgenda"), { ssr: false })
const FacultyMatrix = dynamic(() => import("components/matrix/facultyMatrix"), { ssr: false })
const CourseDirectorsMatrix = dynamic(() => import("components/matrix/courseDirectorsMatrix"), { ssr: false })
const PeopleGridMatrixBlock = dynamic(() => import("components/matrix/peopleGridMatrixBlock"), { ssr: false })
const SocialMediaHome = dynamic(() => import("components/matrix/socialMediaHome"), { ssr: false })

// program-guide specific components — client-only, data-driven
const EmailForm = dynamic(() => import("components/programMatrix/emailForm"), { ssr: false })
const ProgramGuide = dynamic(() => import("components/programMatrix/programGuide"), { ssr: false })
const AtAGlance = dynamic(() => import("components/programMatrix/atAGlance"), { ssr: false })
const DirectorFaculty = dynamic(() => import("components/programMatrix/directorsFaculty"), { ssr: false })
const ProgramChatbot = dynamic(() => import("components/programMatrix/programChatbot"), { ssr: false })

export default function MatrixBlockRenderer({
    blocks,
    site,
    user,
    colorMode = null, // Optional - for [...slug].js usage
    channels = null, // Optional - for agendaSession
    craftSiteId = null, // Optional - for peopleGrid (or derive from site)
    sessionTypes = null, // Optional - for program-guide programGuide block
    sessionId = null, // Optional - for program-guide programGuide block
    presentationId = null, // Optional - for program-guide programGuide block
    facetsConfig = null, // Optional - for program-guide programGuide block
    ad = null, // Optional - ad unit to interleave among the blocks
    adPositionIndex = 0, // Optional - block index the ad is placed at (0 = above first block)
}) {
    // Handle case where blocks is undefined or not an array
    if (!blocks || !Array.isArray(blocks)) {
        console.error("MatrixBlockRenderer: blocks is not an array", blocks)
        return null
    }

    const renderBlock = block => {
        switch (block.type) {
            case "ad": {
                return <Ad block={block} margin="0 auto" />
            }
            // bpdy blocks from html pages, html block from singles
            case "body":
            case "html": {
                return <Body block={block} colorMode={colorMode} padTop="50px" padBottom="50px" margin="0 auto" />
            }
            case "accordion": {
                return <Accordion block={block} colorMode={colorMode} />
            }
            case "tabs": {
                return <Tabs block={block} colorMode={colorMode} />
            }
            case "agendaSession": {
                return <AgendaSession block={block} meetingId={site.meeting.emmaMeetingId} channels={channels} />
            }
            case "advancedAgenda": {
                return <AdvancedAgenda block={block} site={site} />
            }
            case "faculty": {
                return <FacultyMatrix block={block} colorMode={colorMode} meetingId={site.meeting.emmaMeetingId} />
            }
            case "courseDirectors": {
                return <CourseDirectorsMatrix block={block} meetingId={site.meeting.emmaMeetingId} site={site} />
            }
            case "peopleGrid": {
                return (
                    <PeopleGridMatrixBlock
                        block={block}
                        colorMode={colorMode}
                        craftSiteId={craftSiteId || site?.meeting?.siteId}
                    />
                )
            }
            case "socialMedia": {
                return <SocialMediaHome block={block} meetingName={site.meeting.meetingName} />
            }
            case "hero": {
                return <Hero block={block} meetingName={site.meeting.meetingName} />
            }
            case "twoColumn": {
                return <TwoColumn block={block} colorMode={colorMode} />
            }
            case "row": {
                return <Row block={block} colorMode={colorMode} />
            }
            case "wrapper": {
                return <WrapperBlock block={block} colorMode={colorMode} />
            }
            case "customComp":
            case "customComponent": {
                const Component = loadCustomComponent(block.component)
                return <Component block={block} user={user} site={site} colorMode={colorMode} />
            }
            // Program-guide specific block types
            case "pageTitle": {
                return <SinglesH1Heading block={block} />
            }
            case "emailForm": {
                return <EmailForm block={block} />
            }
            case "programGuide": {
                return (
                    <ProgramGuide
                        block={block}
                        sessionTypes={sessionTypes}
                        sessionId={sessionId}
                        presentationId={presentationId}
                        facetsConfig={facetsConfig}
                        site={site}
                    />
                )
            }
            case "atAGlance": {
                return <AtAGlance block={block} site={site} sessionTypes={sessionTypes} />
            }
            case "directorsFaculty": {
                return <DirectorFaculty block={block} site={site} />
            }
            case "programChatbot": {
                return <ProgramChatbot block={block} site={site} />
            }
            default: {
                console.log("Block type not found: " + block.type)
                return null
            }
        }
    }

    const renderedBlocks = blocks.map((block, i) => (
        <BlockStateProvider key={i} block={block}>
            {renderBlock(block)}
        </BlockStateProvider>
    ))

    // interleave the leaderboard ad among the content blocks (0 = above the first block)
    if (ad) {
        const index = Math.min(Math.max(adPositionIndex, 0), renderedBlocks.length)
        renderedBlocks.splice(
            index,
            0,
            <Ad key="layout-ad" block={{ adUnit: ad }} mt={0} bg="transparent" margin="0 auto" />
        )
    }

    return (
        <>
            {/*<Flex as="main" direction="column">*/}
            {renderedBlocks}
            {/*</Flex>*/}
        </>
    )
}
